import React, { useState } from 'react';
import { Search, Filter, X, Calendar, Lock } from 'lucide-react';
import type { DocumentFiltersState, DocumentSourceType } from '../../types/aggregated-documents';
import { aggregatedDocumentsService } from '../../services/aggregated-documents-service';
import { NativeSelect } from "@/components/ui/native-select"
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface DocumentFiltersProps {
  filters: DocumentFiltersState;
  onChange: (filters: DocumentFiltersState) => void;
  onClear: () => void;
}

export const DocumentFilters: React.FC<DocumentFiltersProps> = ({
  filters,
  onChange,
  onClear
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const sourceTypes: DocumentSourceType[] = [
    'CLIENT' as DocumentSourceType,
    'CASE' as DocumentSourceType,
    'TASK' as DocumentSourceType,
    'INTERACTION' as DocumentSourceType,
    'FINANCE' as DocumentSourceType
  ];

  const toggleSourceType = (type: DocumentSourceType) => {
    const current = filters.sourceTypes || [];
    const newTypes = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type];
    onChange({ ...filters, sourceTypes: newTypes });
  };

  const hasActiveFilters =
    filters.search ||
    filters.sourceTypes.length > 0 ||
    filters.documentTypes.length > 0 ||
    filters.onlyConfidential ||
    filters.startDate ||
    filters.endDate;

  return (
    <Card size="sm">
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              type="text"
              aria-label="Buscar documentos"
              placeholder="Buscar documentos..."
              value={filters.search}
              onChange={(e) => onChange({ ...filters, search: e.target.value })}
              className="pl-8"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={showAdvanced ? 'secondary' : 'outline'}
              aria-expanded={showAdvanced}
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <Filter className="size-4" />
              Filtros
            </Button>
            {hasActiveFilters && (
              <Button type="button" variant="ghost" onClick={onClear}>
                <X className="size-4" />
                Limpar
              </Button>
            )}
          </div>
        </div>

        {showAdvanced && (
          <div className="flex flex-col gap-4 border-t pt-4">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">Filtrar por origem:</span>
              <div className="flex flex-wrap gap-2">
                {sourceTypes.map((type) => {
                  const isActive = filters.sourceTypes.includes(type);
                  return (
                    <Button
                      key={type}
                      type="button"
                      size="sm"
                      variant={isActive ? 'default' : 'outline'}
                      aria-pressed={isActive}
                      onClick={() => toggleSourceType(type)}
                    >
                      <span aria-hidden="true">{aggregatedDocumentsService.getSourceTypeIcon(type)}</span>
                      {aggregatedDocumentsService.getSourceTypeLabel(type)}
                    </Button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="documents-filter-start">
                  <Calendar className="size-4 text-muted-foreground" aria-hidden="true" /> Data inicial:
                </Label>
                <Input
                  id="documents-filter-start"
                  type="date"
                  value={filters.startDate ? filters.startDate.toISOString().split('T')[0] : ''}
                  onChange={(e) =>
                    onChange({
                      ...filters,
                      startDate: e.target.value ? new Date(e.target.value) : null
                    })
                  }
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="documents-filter-end">
                  <Calendar className="size-4 text-muted-foreground" aria-hidden="true" /> Data final:
                </Label>
                <Input
                  id="documents-filter-end"
                  type="date"
                  value={filters.endDate ? filters.endDate.toISOString().split('T')[0] : ''}
                  onChange={(e) =>
                    onChange({
                      ...filters,
                      endDate: e.target.value ? new Date(e.target.value) : null
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="documents-filter-sort">Ordenar por:</Label>
                <NativeSelect
                  id="documents-filter-sort"
                  value={filters.sortBy}
                  onChange={(e) =>
                    onChange({
                      ...filters,
                      sortBy: e.target.value as 'date' | 'name' | 'type' | 'size'
                    })
                  }
                >
                  <option value="date">Data</option>
                  <option value="name">Nome</option>
                  <option value="type">Tipo</option>
                  <option value="size">Tamanho</option>
                </NativeSelect>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="documents-filter-order">Ordem:</Label>
                <NativeSelect
                  id="documents-filter-order"
                  value={filters.sortOrder}
                  onChange={(e) =>
                    onChange({
                      ...filters,
                      sortOrder: e.target.value as 'asc' | 'desc'
                    })
                  }
                >
                  <option value="desc">Decrescente</option>
                  <option value="asc">Crescente</option>
                </NativeSelect>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="onlyConfidential"
                checked={filters.onlyConfidential}
                onChange={(e) =>
                  onChange({ ...filters, onlyConfidential: e.target.checked })
                }
                className="size-4 rounded-sm border-input accent-primary"
              />
              <Label htmlFor="onlyConfidential" className="font-normal">
                <Lock className="size-4 text-muted-foreground" aria-hidden="true" />
                Mostrar apenas documentos confidenciais
              </Label>
            </div>
          </div>
        )}

        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 border-t pt-4">
            {filters.search && (
              <Badge variant="secondary" className="pr-1">
                <Search aria-hidden="true" />
                {filters.search}
                <button
                  type="button"
                  aria-label="Remover busca"
                  onClick={() => onChange({ ...filters, search: '' })}
                  className="rounded-full text-muted-foreground hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              </Badge>
            )}

            {filters.sourceTypes.map((type) => (
              <Badge key={type} variant="secondary" className="pr-1">
                {aggregatedDocumentsService.getSourceTypeIcon(type)}{' '}
                {aggregatedDocumentsService.getSourceTypeLabel(type)}
                <button
                  type="button"
                  aria-label={`Remover filtro ${aggregatedDocumentsService.getSourceTypeLabel(type)}`}
                  onClick={() => toggleSourceType(type)}
                  className="rounded-full text-muted-foreground hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              </Badge>
            ))}

            {filters.onlyConfidential && (
              <Badge variant="secondary" className="pr-1">
                <Lock aria-hidden="true" />
                Confidenciais
                <button
                  type="button"
                  aria-label="Remover filtro de confidenciais"
                  onClick={() => onChange({ ...filters, onlyConfidential: false })}
                  className="rounded-full text-muted-foreground hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
